// Dark gradient card surface for canonical document cards and hub links.

import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/utilities/cn';
import { getGradientSurfaceStyle } from '@/styles/gradientSurfaceStyle';
import type { PlayerColorFamily } from '@/styles/playerColors';

export type DocumentGradientCardIcon = React.ComponentType<
  React.SVGProps<SVGSVGElement>
>;

interface DocumentGradientCardProps {
  title: string;
  accentFamily: PlayerColorFamily;
  eyebrow?: string;
  Icon?: DocumentGradientCardIcon;
  className?: string;
  titleClassName?: string;
}

const getDocumentGradientSeed = (
  title: string,
  accentFamily: PlayerColorFamily
): number => {
  // Keep same-card motion deterministic while desyncing sibling cards.
  const value = `${title}:${accentFamily}`;
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  }
  return hash % 3000;
};

const DocumentGradientCard: React.FC<DocumentGradientCardProps> = ({
  title,
  accentFamily,
  eyebrow,
  Icon,
  className,
  titleClassName,
}) => (
  <Card
    className={cn(
      'document-gradient-surface document-gradient-surface--alive relative isolate h-full min-h-32 overflow-hidden border-0 bg-card py-0 text-white ring-1 ring-white/20 transition-all duration-200 group-hover:ring-primary group-hover:shadow-sm',
      className
    )}
    style={getGradientSurfaceStyle(accentFamily, {
      motion: 'alive',
      seed: getDocumentGradientSeed(title, accentFamily),
    })}
  >
    <CardContent className="relative z-10 flex h-full min-h-[inherit] flex-col justify-between p-5 sm:p-6">
      <div className="flex items-start">
        {Icon && <Icon aria-hidden="true" className="size-6 text-white" />}
      </div>
      <div className="flex flex-col items-start gap-1 text-left">
        {eyebrow && (
          <span className="text-sm font-semibold tracking-[0.12em] text-white/80 uppercase sm:text-base">
            {eyebrow}
          </span>
        )}
        <h3
          className={cn(
            'font-heading text-3xl leading-none font-semibold tracking-tight text-white sm:text-4xl',
            titleClassName
          )}
        >
          {title}
        </h3>
      </div>
    </CardContent>
  </Card>
);

export default DocumentGradientCard;
